import React from "react";

function Block({ className = "" }) {
  return <div className={`animate-pulse rounded bg-slate-100 ${className}`} />;
}

function CardShell({ headerTone = "blue", children }) {
  const headerBg =
    headerTone === "amber"
      ? "bg-gradient-to-r from-[#FFFBF5] to-white"
      : "bg-gradient-to-r from-[#F7FAFF] to-white";
  const iconBg = headerTone === "amber" ? "bg-amber-100" : "bg-[#007BFF]/15";

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div
        className={`flex items-center gap-2.5 border-b border-slate-100 px-3 py-2.5 ${headerBg}`}
      >
        <Block className={`h-9 w-9 shrink-0 rounded-lg ${iconBg}`} />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Block className="h-3.5 w-32" />
          <Block className="h-2.5 w-44" />
        </div>
        <Block className="h-8 w-16 rounded-lg" />
      </div>
      <div className="p-3.5">{children}</div>
    </div>
  );
}

function MetricGrid({ count = 4 }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="space-y-1.5">
          <Block className="h-2 w-12" />
          <Block className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

function PlanCardSkeleton({ tone = "blue" }) {
  return (
    <CardShell headerTone={tone}>
      <div className="space-y-4">
        <div
          className={`rounded-xl px-4 py-4 ring-1 ${
            tone === "amber"
              ? "bg-gradient-to-br from-[#FFF8EF] to-white ring-amber-100"
              : "bg-gradient-to-br from-[#F0F7FF] to-white ring-[#007BFF]/10"
          }`}
        >
          <Block className="mb-2 h-2.5 w-28" />
          <Block className="h-8 w-36" />
          <Block className="mt-2 h-2.5 w-48" />
        </div>
        <MetricGrid />
        <div className="border-t border-slate-100 pt-3">
          <Block className="h-3 w-full max-w-md" />
        </div>
      </div>
    </CardShell>
  );
}

function InstallmentsCardSkeleton({ tone = "blue" }) {
  return (
    <CardShell headerTone={tone}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-3">
          <Block className="h-3 w-28" />
          <Block className="h-3 w-32" />
          <Block className="h-3 w-16" />
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-100">
          <div className="grid grid-cols-5 gap-2 border-b border-slate-100 bg-slate-50 px-2.5 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Block key={i} className="h-2.5 w-full" />
            ))}
          </div>
          {Array.from({ length: 4 }).map((_, row) => (
            <div
              key={row}
              className="grid grid-cols-5 gap-2 border-t border-slate-50 px-2.5 py-2.5"
            >
              {Array.from({ length: 5 }).map((_, col) => (
                <Block key={col} className="h-3 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </CardShell>
  );
}

function InquirySkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-2.5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-3 py-2.5">
        <Block className="h-10 w-10 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Block className="h-3.5 w-40" />
          <Block className="h-2.5 w-56" />
        </div>
        <Block className="h-8 w-16 rounded-lg" />
      </div>
      <div className="grid gap-3 p-3.5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Block className="h-2 w-14" />
            <Block className="h-8 w-full rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionLabelSkeleton({ wide = false }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <Block className="h-5 w-1 shrink-0 rounded-full" />
      <Block className={`h-3.5 ${wide ? "w-32" : "w-24"}`} />
      <Block className="h-5 w-24 rounded-full" />
      <Block className="hidden h-px flex-1 sm:block" />
    </div>
  );
}

function CommissionDetailSkeleton() {
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm sm:px-5">
        <div className="mb-2 flex items-center gap-1.5">
          <Block className="h-2.5 w-20" />
          <Block className="h-2 w-2 rounded-full" />
          <Block className="h-2.5 w-12" />
        </div>
        <div className="flex items-end justify-between gap-3">
          <div className="space-y-2">
            <Block className="h-7 w-56 sm:w-72" />
            <Block className="h-3 w-72 max-w-full" />
          </div>
          <Block className="hidden h-6 w-28 rounded-full sm:block" />
        </div>
      </div>

      {/* Inquiry */}
      <InquirySkeleton />

      {/* Commission row */}
      <div>
        <SectionLabelSkeleton />
        <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
          <PlanCardSkeleton tone="blue" />
          <InstallmentsCardSkeleton tone="blue" />
        </div>
      </div>

      {/* Bonus row */}
      <div>
        <SectionLabelSkeleton wide />
        <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
          <PlanCardSkeleton tone="amber" />
          <InstallmentsCardSkeleton tone="amber" />
        </div>
      </div>
    </div>
  );
}

export default CommissionDetailSkeleton;
